import _ from 'lodash';
import { generateContentId } from './contentId';
import { getMediaValue } from './contentAllocator';
import { interpolateSingleValue } from './contentInterpolator';
import { ContentMapCreator } from './contentMapCreator';
import { CeField } from './enums/CeFiled';
var DefaultSectionKey = ContentMapCreator.DefaultSectionKey;
function calcTpaMetaSiteIds(contentItems) {
    return _.reduce(contentItems, function (map, v, k) {
        if (v.metasite) {
            map[k] = v.metasite[0].content;
        }
        return map;
    }, {});
}
export var MEDIA_OFFSET = 3;
var ContentItemsGenerator = /** @class */ (function () {
    function ContentItemsGenerator(contentItems, replacementsWithPlaceholders, replacementsWithDefaults, locale, geo) {
        this.setItems = null;
        this.setContent = {};
        this.contentItems = contentItems;
        this.replacementsWithPlaceholders = replacementsWithPlaceholders;
        this.replacementsWithDefaults = replacementsWithDefaults;
        this.tpaMetasitesIDs = calcTpaMetaSiteIds(contentItems);
        this.locale = locale;
        this.geo = geo;
    }
    ContentItemsGenerator.prototype.updateReplacements = function (contentItems, replacementsWithPlaceholders, replacementsWithDefaults) {
        this.contentItems = contentItems;
        this.replacementsWithPlaceholders = replacementsWithPlaceholders;
        this.replacementsWithDefaults = replacementsWithDefaults;
    };
    ContentItemsGenerator.prototype.generate = function (id, count, offset) {
        return this.generateContentItems(id, count, offset);
    };
    ContentItemsGenerator.prototype.generateList = function (id, count, cardinality, offset) {
        return this.generateListContentItems(id, count, cardinality, offset);
    };
    ContentItemsGenerator.prototype.generateContentItems = function (id, count, offset, specificContext, context) {
        var _this = this;
        if (count === void 0) { count = 1; }
        if (offset === void 0) { offset = 0; }
        if (specificContext === void 0) { specificContext = {}; }
        if (context === void 0) { context = { self: specificContext }; }
        var ceType = id.ceType, ceTypeAlias = id.ceTypeAlias;
        var contentItems = this.resolveContentItemsByCeType(ceType, ceTypeAlias);
        var generatedContentItems = [];
        var _loop_1 = function (i) {
            var fields = {};
            // field order is important, do not change!
            [
                CeField.title,
                CeField.subTitle,
                CeField.longText,
                CeField.image,
                CeField.button,
            ].forEach(function (field, index) {
                var fieldValue = null;
                // always add offset, it allows to "randomize" items outside
                var valueIndex = i + index + offset;
                var values = _this.getItemValuesForField(contentItems, field, valueIndex);
                if (values.itemValues) {
                    if (field === CeField.image) {
                        fieldValue = _this.getMediaFieldValue(values, valueIndex);
                    }
                    else {
                        fieldValue = _this.getFieldValue(values, valueIndex, context);
                    }
                }
                if (field === CeField.image) {
                    // image field can include videos, images and shapes, so rename it to media as more suitable name
                    fields.media = fieldValue;
                }
                else {
                    fields[field] = fieldValue;
                }
                specificContext[field] = fieldValue;
            });
            generatedContentItems.push(fields);
        };
        for (var i = 0; i < count; i++) {
            _loop_1(i);
        }
        // reset sets after we generated all content
        this.resetSets();
        return generatedContentItems;
    };
    ContentItemsGenerator.prototype.generateListContentItems = function (id, count, cardinality, offset) {
        var _this = this;
        if (count === void 0) { count = 1; }
        if (cardinality === void 0) { cardinality = 3; }
        if (offset === void 0) { offset = 0; }
        var context = {
            self: {
                items: new Array(cardinality).fill(null).map(function () { return ({}); }),
                header: {},
                footer: {},
            },
        };
        var ceType = id.ceType, ceTypeAlias = id.ceTypeAlias;
        var ceTypeHeader = createListCeType(ceType, true);
        var ceTypeAliasHeader = createListCeType(ceTypeAlias, true);
        var computedIdHeader = generateContentId({
            ceType: ceTypeHeader,
            id: ceTypeAliasHeader,
        });
        var ceTypeFooter = createListCeType(ceType);
        var ceTypeAliasFooter = createListCeType(ceTypeAlias);
        var computedIdFooter = generateContentId({
            ceType: ceTypeFooter,
            id: ceTypeAliasFooter,
        });
        return Array(count)
            .fill(null)
            .map(function (v, index) { return ({
            items: _.range(0, cardinality).map(function (i) {
                return _this.generateContentItems(id, 1, index + i + offset, context.self.items[i], {
                    self: context.self.items[i],
                })[0];
            }),
            header: _this.generateContentItems(computedIdHeader, 1, index + offset, context.self.header, context)[0],
            footer: _this.generateContentItems(computedIdFooter, 1, index + offset, context.self.footer, context)[0],
        }); });
    };
    ContentItemsGenerator.prototype.getMediaFieldValue = function (resolvedValues, contentIndex) {
        var mediaList = [];
        if (resolvedValues.setValues) {
            mediaList = getMediaValue(resolvedValues.setValues, contentIndex + MEDIA_OFFSET, this.locale, this.geo, false, []);
        }
        var usedMediaTypes = _.uniq(_.map(mediaList, function (mediaItem) { return mediaItem.mediaType; }));
        mediaList = mediaList.concat(getMediaValue(resolvedValues.itemValues, contentIndex, this.locale, this.geo, true, usedMediaTypes));
        return mediaList;
    };
    ContentItemsGenerator.prototype.getItemValuesForField = function (contentItems, field, contentIndex) {
        var fieldValues = contentItems && contentItems[field];
        var defaultValues = this.contentItems[DefaultSectionKey];
        var defaultFieldValues = _.cloneDeep(_.get(defaultValues, field));
        var setValues = this.tryGetSetValues(field);
        var newItemValues = _.cloneDeep(fieldValues) || defaultFieldValues;
        if (!setValues && !this.setItems) {
            newItemValues = this.setupSetsIfNeeded(contentItems, fieldValues, defaultFieldValues, contentIndex);
        }
        if (field === CeField.image) {
            this.completeMissingMediaTypes(newItemValues, defaultFieldValues);
        }
        return { itemValues: newItemValues, setValues: setValues };
    };
    ContentItemsGenerator.prototype.setupSetsIfNeeded = function (contentItemElements, fieldValues, defaultFieldValues, contentIndex) {
        var itemValues = null;
        var fieldValueNormalizedIndex = fieldValues
            ? contentIndex % fieldValues.length
            : 0;
        if (fieldValues &&
            fieldValues.length > 0 &&
            fieldValues[fieldValueNormalizedIndex].setItems) {
            this.setItems = fieldValues[fieldValueNormalizedIndex].setItems;
            this.setContent = contentItemElements;
            itemValues = fieldValues;
        }
        else if (defaultFieldValues &&
            defaultFieldValues.length > 0 &&
            defaultFieldValues[0].setItems) {
            this.setItems = defaultFieldValues[0].setItems;
            this.setContent = _.cloneDeep(this.contentItems[DefaultSectionKey]);
            itemValues = defaultFieldValues;
        }
        else {
            itemValues = fieldValues || defaultFieldValues;
        }
        return itemValues;
    };
    ContentItemsGenerator.prototype.tryGetSetValues = function (field) {
        var setItem = this.setItems
            ? _.find(this.setItems, { field: field })
            : null;
        var setValue = setItem && this.setContent[field][setItem.index];
        return setValue ? [setValue] : null;
    };
    ContentItemsGenerator.prototype.getFieldValue = function (values, offset, context) {
        var valuesToUse = values.setValues || values.itemValues;
        var finalValues = this.createSortedScoresWithSelfValues(valuesToUse, context);
        var value = finalValues[offset % finalValues.length];
        if (value && value.setItems) {
            this.setItems = value.setItems; // update the set items we used for next fields
        }
        return value ? value.content : null;
    };
    ContentItemsGenerator.prototype.resetSets = function () {
        this.setItems = null; // reset sets before we start iterating
        this.setContent = {};
    };
    ContentItemsGenerator.prototype.createSortedScoresWithSelfValues = function (itemValues, context) {
        var _this = this;
        var items = _.map(itemValues, function (item) {
            return _this.interpolateNonFinal(item, context);
        }).filter(function (item) { return item.isFinal; });
        return _.sortBy(items, function (item) { return -item.score; });
    };
    ContentItemsGenerator.prototype.interpolateNonFinal = function (item, context) {
        if (!item.isFinal) {
            var interpolatedWithPreviousValues = interpolateSingleValue(item.content, context, _.merge(this.replacementsWithPlaceholders, context), _.merge(this.replacementsWithDefaults, context));
            interpolatedWithPreviousValues.score *= item.score;
            return interpolatedWithPreviousValues;
        }
        return item;
    };
    ContentItemsGenerator.prototype.completeMissingMediaTypes = function (srcItems, defaultItems) {
        if (!srcItems) {
            return;
        }
        var existingMediaTypes = this.getAllMediaTypes(srcItems);
        var _a = this.scoreMissingMediaByType(defaultItems, existingMediaTypes), content = _a.content, maxScoreByType = _a.maxScoreByType;
        this.addMissingMediaByTypeScore(srcItems, content, maxScoreByType);
    };
    ContentItemsGenerator.prototype.addMissingMediaByTypeScore = function (items, missingItems, maxScoreByType) {
        _.forEach(missingItems, function (entry) {
            var newEntry = _.cloneDeep(entry);
            _.remove(newEntry.content, function (mediaItem) {
                var mediaTypeScore = maxScoreByType[mediaItem.mediaType];
                return entry.score < mediaTypeScore;
            });
            if (Array.isArray(newEntry.content) && newEntry.content.length > 0) {
                items.push(newEntry);
            }
        });
    };
    ContentItemsGenerator.prototype.removeContentEntriesByMediaType = function (entry, mediaTypes) {
        var newEntry = _.cloneDeep(entry);
        _.remove(newEntry.content, function (mediaItem) {
            return _.includes(mediaTypes, mediaItem.mediaType);
        });
        return newEntry;
    };
    ContentItemsGenerator.prototype.scoreMissingMediaByType = function (items, existingMediaTypes) {
        var _this = this;
        var content = [];
        var maxScoreByType = {};
        if (!items) {
            return { content: content, maxScoreByType: maxScoreByType };
        }
        items.forEach(function (entry) {
            var entryWithMissingMediaTypes = _this.removeContentEntriesByMediaType(entry, existingMediaTypes);
            if (Array.isArray(entryWithMissingMediaTypes.content) &&
                entryWithMissingMediaTypes.content.length) {
                content.push(entryWithMissingMediaTypes);
                entryWithMissingMediaTypes.content.forEach(function (mediaItem) {
                    var mediaTypeMaxScore = _.get(maxScoreByType, mediaItem.mediaType, 0);
                    if (entry.score > mediaTypeMaxScore) {
                        maxScoreByType[mediaItem.mediaType] = entry.score;
                    }
                });
            }
        });
        return { content: content, maxScoreByType: maxScoreByType };
    };
    ContentItemsGenerator.prototype.getAllMediaTypes = function (items) {
        var allTypes = items.map(function (entry) {
            return _.map(entry.content, function (mediaItem) { return mediaItem.mediaType; });
        });
        return _(allTypes).flatten().uniq().value();
    };
    ContentItemsGenerator.prototype.handleAliasContentOverride = function (itemElements, ceType) {
        var specificContentItemElements = this.contentItems[ceType] || {};
        _.keys(specificContentItemElements).forEach(function (key) {
            // if no source, source with setItems or specific with setItems
            var baseType = key in itemElements;
            var setOnBaseType = baseType
                ? _.some(itemElements[key], function (entry) { return !!entry.setItems; })
                : false;
            var setOnAlias = _.some(specificContentItemElements[key], function (entry) { return !!entry.setItems; });
            if (!baseType || !setOnBaseType || setOnAlias) {
                itemElements[key] = specificContentItemElements[key];
            }
        });
    };
    ContentItemsGenerator.prototype.resolveContentItemsByCeType = function (ceType, ceTypeAlias) {
        var contentItemByCeType = ceType in this.contentItems ? _.cloneDeep(this.contentItems[ceType]) : {};
        var originalContentItemsByCeType = _.cloneDeep(contentItemByCeType);
        if (ceTypeAlias !== ceType) {
            this.handleAliasContentOverride(contentItemByCeType, ceTypeAlias);
        }
        removeLowerScoredContent(contentItemByCeType);
        this.completeMissingMediaTypes(contentItemByCeType.image, originalContentItemsByCeType.image);
        return contentItemByCeType;
    };
    ContentItemsGenerator.prototype.getTpaMetaSiteIds = function () {
        return this.tpaMetasitesIDs;
    };
    ContentItemsGenerator.prototype.contentMapItemToTextData = function (sectionsNames, content, textGroups) {
        var allTexts = [];
        _.each(textGroups, function (textGroup) {
            var textArray = content[textGroup];
            if (textArray) {
                _.each(textArray, function (textData) {
                    allTexts.push({
                        text: textData.content,
                        sectionTypesData: sectionsNames.map(function (sectionType) {
                            return {
                                sectionType: sectionType,
                                relevance: textData.score || 0,
                            };
                        }),
                        textGroup: textGroup,
                    });
                });
            }
        });
        return allTexts;
    };
    ContentItemsGenerator.prototype.getContentTextData = function () {
        var _this = this;
        var allTexts = [];
        var supportedFields = [CeField.title, CeField.longText, CeField.subTitle];
        _.each(this.contentItems, function (textFieldsToData, key) {
            allTexts = allTexts.concat(_this.contentMapItemToTextData([key], textFieldsToData, supportedFields));
        });
        return allTexts;
    };
    return ContentItemsGenerator;
}());
export { ContentItemsGenerator };
function createListCeType(ceType, isHeader) {
    if (isHeader === void 0) { isHeader = false; }
    if (isHeader) {
        return ceType + '_ListHeader';
    }
    return ceType + '_ListFooter';
}
function removeLowerScoredContent(itemElements) {
    _.keys(itemElements).forEach(function (key) {
        var maxContentScore = 0;
        var maxSetScore = 0;
        _.forEach(itemElements[key], function (entry) {
            if (entry.setItems && entry.score > maxSetScore) {
                maxSetScore = entry.score;
            }
            if (!entry.setItems && entry.score > maxContentScore) {
                maxContentScore = entry.score;
            }
        });
        // if we have a set, keep it
        if (maxSetScore > 0) {
            itemElements[key] = _.filter(itemElements[key], function (entry) {
                return !!(entry.score === maxSetScore && entry.setItems);
            }).map(function (entry, index) {
                // adjust set index if the upper filter fucked the order
                var fieldEntry = _.find(entry.setItems, function (item) { return item.field === key; });
                if (fieldEntry && fieldEntry.index > index) {
                    fieldEntry.index = index;
                }
                return entry;
            });
        }
        else if (maxContentScore > 0) {
            // otherwise keep best content by max score we found
            itemElements[key] = _.filter(itemElements[key], function (entry) { return entry.score === maxContentScore; });
        }
    });
}
//# sourceMappingURL=contentItemGenerator.js.map